/**
 * Medicare premium + IRMAA constants for 2026 (spec §9/§11).
 *
 * IRMAA is triggered by MAGI from TWO YEARS PRIOR (2026 premiums look at the
 * 2024 return), and the tiers are CLIFFS, not phase-ins — one dollar over a
 * threshold costs the full step.
 *
 * Sources (researched 2026-08-03): CMS 2026 premium announcement as reported
 * by Kiplinger / TheFinanceBuff / FederalPensionAdvisors. Married-filing-
 * separately uses its own harsher schedule — approximated here by the top
 * tiers; VERIFY before relying on MFS specifics.
 */
import type { FilingStatus } from '../../model/types';

export const MEDICARE_CONSTANTS_AS_OF = '2026-08-03';

/** 2026 standard Part B premium (monthly). */
export const PART_B_BASE_2026 = 202.9;

/** 2026 Part D national base beneficiary premium (CMS, 2025-07-28). */
export const PART_D_BASE_2026 = 38.99;

export interface IrmaaTier {
  /** MAGI floor for this tier (exclusive of lower tiers). */
  magiFloorSingle: number;
  magiFloorMfj: number;
  /** TOTAL monthly Part B premium at this tier (base + surcharge). */
  partBTotal: number;
  /** Monthly Part D surcharge added on top of the plan premium. */
  partDSurcharge: number;
}

/** 2026 tiers, ordered. Tier 0 is the base (no surcharge). */
export const IRMAA_TIERS_2026: IrmaaTier[] = [
  { magiFloorSingle: 0,        magiFloorMfj: 0,        partBTotal: 202.9,  partDSurcharge: 0 },
  { magiFloorSingle: 109_000,  magiFloorMfj: 218_000,  partBTotal: 284.1,  partDSurcharge: 14.5 },
  { magiFloorSingle: 137_000,  magiFloorMfj: 274_000,  partBTotal: 405.8,  partDSurcharge: 37.5 },
  { magiFloorSingle: 171_000,  magiFloorMfj: 342_000,  partBTotal: 527.5,  partDSurcharge: 60.4 },
  { magiFloorSingle: 205_000,  magiFloorMfj: 410_000,  partBTotal: 649.2,  partDSurcharge: 83.3 },
  { magiFloorSingle: 500_000,  magiFloorMfj: 750_000,  partBTotal: 689.9,  partDSurcharge: 91.0 },
];

export interface IrmaaResult {
  tierIndex: number; // 0 = no surcharge
  partBMonthly: number;
  partDSurchargeMonthly: number;
  /** MAGI headroom to the NEXT tier threshold (Infinity at the top). */
  headroomToNextTier: number;
}

/**
 * Determine the IRMAA tier from the LOOKBACK-year MAGI (two years prior).
 * The caller is responsible for passing the right year's MAGI.
 *
 * `thresholdScale` inflates the MAGI floors for future years — IRMAA tiers are
 * CPI-indexed annually (the top tier's freeze ended in 2028), so comparing a
 * far-future nominal MAGI against 2026 floors would falsely put nearly every
 * retiree in surcharge territory. Premium DOLLARS stay in 2026 terms; the
 * caller applies healthcare inflation to those.
 */
export function irmaaFor(
  lookbackMagi: number,
  filingStatus: FilingStatus,
  thresholdScale = 1,
): IrmaaResult {
  // MFJ/QW use joint thresholds; single/HOH (and, approximately, MFS) single's.
  const joint = filingStatus === 'mfj' || filingStatus === 'qw';
  const floorOf = (t: IrmaaTier) => (joint ? t.magiFloorMfj : t.magiFloorSingle) * thresholdScale;
  let tierIndex = 0;
  for (let i = 1; i < IRMAA_TIERS_2026.length; i++) {
    if (lookbackMagi > floorOf(IRMAA_TIERS_2026[i]!)) tierIndex = i;
  }
  const tier = IRMAA_TIERS_2026[tierIndex]!;
  const next = IRMAA_TIERS_2026[tierIndex + 1];
  const headroom = next ? floorOf(next) - lookbackMagi : Infinity;
  return {
    tierIndex,
    partBMonthly: tier.partBTotal,
    partDSurchargeMonthly: tier.partDSurcharge,
    headroomToNextTier: headroom,
  };
}
